'use client';
import { useState, useSyncExternalStore } from 'react';

const noopSubscribe = () => () => {};

interface Props {
  portfolioSymbols: Set<string>;
  onPortfolioChange: (symbols: Set<string>) => void;
  portfolioIsolated: boolean;
  onTogglePortfolioIsolate: () => void;
}

export function PortfolioPanel({
  portfolioSymbols,
  onPortfolioChange,
  portfolioIsolated,
  onTogglePortfolioIsolate,
}: Props) {
  const [portfolioInput, setPortfolioInput] = useState('');
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const mounted = useSyncExternalStore(noopSubscribe, () => true, () => false);

  const addInputSymbols = () => {
    if (!portfolioInput.trim()) return;
    const symbols = portfolioInput.split(/[,\s]+/).filter(Boolean);
    const next = new Set(portfolioSymbols);
    symbols.forEach(s => next.add(s.trim()));
    onPortfolioChange(next);
    setPortfolioInput('');
  };

  return (
    <div className="px-5 py-2 border-b border-[#1e293b]">
      <button
        onClick={() => {
          if (mounted && portfolioSymbols.size === 0) {
            setPortfolioOpen(prev => !prev);
            return;
          }
          const nextIsolated = !portfolioIsolated;
          onTogglePortfolioIsolate();
          setPortfolioOpen(nextIsolated);
        }}
        className={`flex items-center justify-between w-full cursor-pointer rounded-lg
          ${portfolioIsolated ? 'ring-1 ring-blue-500/50 bg-blue-500/5 px-2 -mx-2 py-1.5 -my-1.5' : ''}`}
        title={portfolioIsolated ? 'Click to collapse & show all stocks' : 'Click to show only your portfolio'}
      >
        <div className="flex items-center gap-2">
          <label className={`text-[10px] font-semibold uppercase tracking-widest cursor-pointer ${portfolioIsolated ? 'text-blue-400' : 'text-[#e2e8f0]'}`}>
            My Portfolio
          </label>
          {mounted && portfolioSymbols.size > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-semibold tabular-nums">
              {portfolioSymbols.size}
            </span>
          )}
          {portfolioIsolated && (
            <span className="text-[8px] text-blue-400 uppercase tracking-wider">isolated</span>
          )}
        </div>
        <svg
          className={`w-3 h-3 text-white transition-transform duration-150 ${portfolioOpen ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {portfolioOpen && (
        <div className="mt-3">
          <div className="flex gap-1.5 mb-2">
            <input
              type="text"
              value={portfolioInput}
              onChange={e => setPortfolioInput(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === 'Enter') addInputSymbols(); }}
              placeholder="AAPL, MSFT, NVDA..."
              className="flex-1 bg-[#111827] text-white border border-[#1e293b] rounded-lg px-2.5 py-1.5 text-[11px]
                         placeholder-[#334155] focus:outline-none focus:border-blue-500/50
                         transition-all duration-150"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
            <button
              onClick={addInputSymbols}
              className="px-2 py-1.5 text-[10px] font-semibold bg-blue-500/20 text-blue-400
                         rounded-lg hover:bg-blue-500/30 transition-colors duration-150 cursor-pointer"
            >
              Add
            </button>
          </div>

          {portfolioSymbols.size > 0 && (
            <div className="flex flex-wrap gap-1">
              {[...portfolioSymbols].sort().map(sym => (
                <span key={sym} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#1e293b] text-[10px] text-[#e2e8f0]"
                  style={{ fontFamily: 'var(--font-mono)' }}>
                  {sym}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = new Set(portfolioSymbols);
                      next.delete(sym);
                      onPortfolioChange(next);
                    }}
                    className="text-white hover:text-red-400 cursor-pointer ml-0.5"
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
